import React, { useEffect, useState, useCallback } from 'react';
import { getAdminProjects, toggleProjectStatus } from '../../services/adminApi';
import ReusableAdminTable from './ReusableAdminTable';
import { deleteProjectApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import useProjectColumns from './projects/components/useProjectColumns';

const AdminProjectList = () => {
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    
    const navigate = useNavigate();

    const handleEdit = useCallback((projectId) => {
        if (!projectId) return alert("Project ID not found.");
        navigate(`/admin/post-projects/${projectId}`);
    }, [navigate]);

    const handleAddNew = () => {
        navigate('/admin/post-projects');
    };

    const loadProjects = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const res = await getAdminProjects();
            if (res && res.data) {
                setProjects(res.data);
            } else {
                setProjects([]);
                setError('No projects found or API returned unexpected data.');
            }
        } catch (err) {
            console.error("Error fetching projects:", err);
            setError('Failed to load projects. Please try again later.');
            setProjects([]);
        } finally {
            setIsLoading(false);
        }
    }, []); 

    useEffect(() => {
        loadProjects();
    }, [loadProjects]); 

    const handleDelete = useCallback(async (projectIdToDelete) => {
        if (!projectIdToDelete) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Project',
                text: 'Project ID not found.',
                background: '#111827',
                color: '#fff'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Delete Project?',
            text: 'This may also affect associated roles.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#c30000',
            cancelButtonColor: '#374151',
            background: '#111827',
            color: '#fff'
        });

        if (!result.isConfirmed) return;

        try {
            await deleteProjectApi(`/projects/${projectIdToDelete}`);

            await Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Project deleted successfully.',
                timer: 1800,
                showConfirmButton: false,
                background: '#111827',
                color: '#fff'
            });

            loadProjects();
        } catch (err) {
            console.error("Error deleting project:", err);
            Swal.fire({
                icon: 'error',
                title: 'Deletion Failed',
                text: 'Please try again later.',
                background: '#111827',
                color: '#fff'
            });
        }
    }, [loadProjects]);

    const handleToggleStatus = useCallback(async (id, status) => {
        try {
            const isActive = status === 'active';

            const result = await Swal.fire({
                title: isActive ? 'Deactivate Project?' : 'Activate Project?',
                text: isActive ? 'All roles under this project will also be deactivated.' : 'This project will become active.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: isActive ? 'Yes, Deactivate' : 'Yes, Activate',
                cancelButtonText: 'Cancel',
                confirmButtonColor: isActive ? '#c30000' : '#16a34a',
                cancelButtonColor: '#374151',
                background: '#111827',
                color: '#fff'
            });

            if (!result.isConfirmed) return;

            await toggleProjectStatus(id);

            await Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: isActive ? 'Project deactivated successfully.' : 'Project activated successfully.',
                timer: 1800,
                showConfirmButton: false,
                background: '#111827',
                color: '#fff'
            });

            loadProjects();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: 'Failed to update project status.',
                background: '#111827',
                color: '#fff'
            });
        }
    }, [loadProjects]);

    const columns = useProjectColumns({ handleToggleStatus, handleEdit, handleDelete });

    return (
        <ReusableAdminTable
            data={projects}
            columns={columns}
            isLoading={isLoading}
            title="Project Management"
            onAddNew={handleAddNew}
            addNewButtonText="Add Project"
            searchPlaceholder="Search projects..."
            exportCsvFilename="projects.csv"
        />
    );
};

export default AdminProjectList;